/** '2mo 4d' · '63d 21h' · '2h 10m' · '14m' · '< 1m'. Never '0m' for missing
 * data — pass null, render '—'.
 *
 * Lifted out of features/reports/format.ts (which now re-exports it) when the
 * phase 34 inbox needed the same rule for its SLA column. One definition of
 * "how long is this", so an SLA countdown and a resolution median never
 * disagree about what 90 minutes looks like.
 *
 * The unit changes twice, each time at the point the previous one stops being
 * countable at a glance:
 *
 *  - Past a day, hours give way to days. A breached ticket reading
 *    `1533h 46m over` is a number nobody converts — it is 63 days, and the
 *    minutes on it are noise at that scale, so they are dropped rather than
 *    carried. 24h is the threshold the rest of the product already uses for
 *    this (analytics.service.ts `formatDuration`).
 *  - Past a month, days give way to months plus the days left over — `69d 18h`
 *    is the same unreadable arithmetic one level up. The leftover hours are
 *    rounded into a day (12h or more counts as one) because an hour is not a
 *    figure anyone reads off a two-month breach, and the rounding happens
 *    BEFORE the split so a rounded-up day can carry into the next month rather
 *    than printing '1mo 30d'.
 *
 * A month here is a flat 30 days. Calendar months are 28–31 long, so no exact
 * answer exists for "how many months is 69 days"; 30 keeps the figure stable
 * and monotonic, which matters more on a queue than calendar fidelity.
 */
const DAYS_PER_MONTH = 30;

export function duration(seconds: number | null): string {
  if (seconds === null) return '—';
  if (seconds < 60) return '< 1m';
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  if (h >= 24) {
    const d = Math.floor(h / 24);
    const rest = h % 24;
    if (d >= DAYS_PER_MONTH) {
      const totalDays = d + (rest >= 12 ? 1 : 0);
      const months = Math.floor(totalDays / DAYS_PER_MONTH);
      const days = totalDays % DAYS_PER_MONTH;
      return days ? `${months}mo ${days}d` : `${months}mo`;
    }
    return rest ? `${d}d ${rest}h` : `${d}d`;
  }
  return h ? `${h}h${m ? ` ${m}m` : ''}` : `${m}m`;
}
